/**
 * Daily puzzle page - Single View App Architecture
 * 
 * Renders either:
 * - Puzzle view (when status is in_progress)
 * - GameOver view (when status is solved/missed/revealed)
 * 
 * No URL navigation - all state transitions happen inline.
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { createEngine } from '@daily-puzzle/rules';
import type { DailyResponse } from '@daily-puzzle/shared';
import { Status } from '@daily-puzzle/shared';
import { fetchDaily } from '../api/client';
import { Board } from '../components/Board';
import { InstrumentHeader } from '../components/InstrumentHeader';
import { BoardNotification } from '../components/BoardNotification/BoardNotification';
import { GameOverView } from '../components/GameOverView';
import { Button } from '../components/ui';
import { useGameState } from '../hooks/useGameState';
import { useShake } from '../hooks/useShake';
import { useHaptics } from '../hooks/useHaptics';
import { useApp } from '../context/AppContext';
import { trackPuzzleStarted } from '../lib/analytics';
import './Daily.css';

export function Daily() {
    const { date } = useParams<{ date?: string }>();
    const navigate = useNavigate();
    const { refreshKey, triggerRefresh } = useApp();

    const [dailyData, setDailyData] = useState<DailyResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [progress, setProgress] = useState({ status: Status.InProgress, attemptsUsed: 0, stepIndex: 0, streak: undefined as number | undefined, solveTimeSeconds: undefined as number | undefined });

    // Track latest FEN for GameOver board view
    const latestFenRef = useRef<string | null>(null);

    // Hook for shaking & haptics
    const { shakeClassName, triggerShake } = useShake();
    const { triggerHaptic } = useHaptics();

    // Load daily data - refreshKey triggers refetch when date changes (app-like behavior)
    useEffect(() => {
        async function load() {
            setLoading(true);
            setError(null);
            try {
                const data = await fetchDaily(date);
                setDailyData(data);

                const loadedStatus = data.progress.status as Status;
                setProgress({
                    status: loadedStatus,
                    attemptsUsed: data.progress.attempts_used,
                    stepIndex: data.progress.step_index,
                    streak: data.current_streak,
                    solveTimeSeconds: data.progress.solve_time_seconds,
                });
                if (!data.is_replay && loadedStatus === Status.InProgress && data.progress.step_index === 0) {
                    trackPuzzleStarted(data.date, data.puzzle.attacker, data.puzzle.steps.length);
                }
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Failed to load puzzle');
            } finally {
                setLoading(false);
            }
        }
        load();
    }, [date, refreshKey]);

    // Handle status changes from game state
    const handleStatusChange = useCallback((status: Status, attemptsUsed: number, stepIndex: number, streak?: number, solveTimeSeconds?: number) => {
        setProgress({ status, attemptsUsed, stepIndex, streak, solveTimeSeconds });
    }, []);

    // Wrappers to intercept failure for shaking
    const onStatusChangeWithShake = useCallback((status: Status, attemptsUsed: number, stepIndex: number, streak?: number, solveTimeSeconds?: number) => {
        // If attempts increased and still in progress, it was a mistake -> Shake + Haptic
        if (status === Status.InProgress && attemptsUsed > progress.attemptsUsed) {
            triggerShake();
            triggerHaptic(200);
        }
        // Solved -> Haptic Pattern
        if (status === Status.Solved) {
            triggerHaptic([50, 50, 50]);
        }
        handleStatusChange(status, attemptsUsed, stepIndex, streak, solveTimeSeconds);
    }, [handleStatusChange, progress.attemptsUsed, triggerShake, triggerHaptic]);

    const { gameState, fen, lastMove, legalMoves, handleMove, toast } = useGameState(
        dailyData,
        onStatusChangeWithShake
    );

    // Trigger shake on illegal moves (toast)
    useEffect(() => {
        if (toast?.isError) {
            triggerShake();
        }
    }, [toast, triggerShake]);

    // Track latest FEN for GameOver board view
    useEffect(() => {
        if (fen) {
            latestFenRef.current = fen;
        }
    }, [fen]);

    // Handle new day callback from GameOverView
    const handleNewDay = useCallback(() => {
        // Reset progress to trigger refetch
        setProgress({ status: Status.InProgress, attemptsUsed: 0, stepIndex: 0, streak: undefined, solveTimeSeconds: undefined });
        triggerRefresh();
    }, [triggerRefresh]);

    if (loading) {
        return (
            <div className="daily-page">
                <div className="loading-state">
                    <span>Loading puzzle...</span>
                </div>
            </div>
        );
    }

    if (error || !dailyData) {
        return (
            <div className="daily-page">
                <div className="error-state">
                    <span>{error || 'Failed to load puzzle'}</span>
                    <Button className="retry-button" width="fixed" onClick={() => window.location.reload()}>
                        Retry
                    </Button>
                </div>
            </div>
        );
    }

    if (dailyData.is_replay) {
        return (
            <ReplayView
                dailyData={dailyData}
                onBackToDaily={() => navigate('/daily')}
            />
        );
    }

    const isFinished = progress.status !== Status.InProgress;
    const isAnimating = gameState.type === 'animating';

    // GAME OVER VIEW - Rendered inline when puzzle is finished
    if (isFinished) {
        return (
            <GameOverView
                status={progress.status}
                attemptsUsed={progress.attemptsUsed}
                puzzleDate={dailyData.date}
                fen={dailyData.puzzle.start_fen}
                finalFen={latestFenRef.current ?? dailyData.puzzle.start_fen}
                orientation={dailyData.puzzle.attacker === 'w' ? 'white' : 'black'}
                totalSteps={dailyData.puzzle.steps.length}
                attacker={dailyData.puzzle.attacker}
                shareText={dailyData.share.text}
                effectiveDailyDate={dailyData.effective_daily_date}
                streak={progress.streak ?? dailyData.current_streak}
                solveTimeSeconds={progress.solveTimeSeconds ?? dailyData.progress.solve_time_seconds}
                onNewDay={handleNewDay}
            />
        );
    }

    // PUZZLE VIEW - Active gameplay
    return (
        <div className="daily-page">
            <div className={`page-content ${shakeClassName}`}>
                <InstrumentHeader
                    puzzleDate={dailyData.date}
                    attacker={dailyData.puzzle.attacker}
                    currentStep={progress.stepIndex}
                    totalSteps={dailyData.puzzle.steps.length}
                    attemptsUsed={progress.attemptsUsed}
                    status={progress.status}
                />

                <Board
                    fen={fen || dailyData.puzzle.start_fen}
                    orientation={dailyData.puzzle.attacker === 'w' ? 'white' : 'black'}
                    onMove={handleMove}
                    lastMove={lastMove ?? undefined}
                    legalMoves={legalMoves}
                    disabled={isFinished || isAnimating}
                />
            </div>

            <BoardNotification
                message={toast?.message || ''}
                visible={!!toast?.isError}
            />

            {toast && !toast.isError && (
                <div className="toast">
                    {toast.message}
                </div>
            )}
        </div>
    );
}

function ReplayView({
    dailyData,
    onBackToDaily,
}: {
    dailyData: DailyResponse;
    onBackToDaily: () => void;
}) {
    const solutionUci = useMemo(
        () => dailyData.puzzle.steps.flatMap((step) => (
            step.auto_reply_uci
                ? [step.your_moves_uci[0]!, step.auto_reply_uci]
                : [step.your_moves_uci[0]!]
        )),
        [dailyData.puzzle.steps]
    );
    const [boardFen, setBoardFen] = useState(dailyData.puzzle.start_fen);
    const [lastMove, setLastMove] = useState<{ from: string; to: string } | null>(null);
    const [isReplaying, setIsReplaying] = useState(false);

    useEffect(() => {
        setBoardFen(dailyData.puzzle.start_fen);
        setLastMove(null);
        setIsReplaying(false);
    }, [dailyData.date, dailyData.puzzle.start_fen]);

    useEffect(() => {
        if (!isReplaying) return;

        let cancelled = false;
        const runReplay = async () => {
            const engine = createEngine(dailyData.puzzle.start_fen);
            setBoardFen(dailyData.puzzle.start_fen);
            setLastMove(null);

            for (const move of solutionUci) {
                await new Promise((resolve) => setTimeout(resolve, 700));
                if (cancelled) return;
                engine.makeMove(move);
                setBoardFen(engine.fen());
                setLastMove({ from: move.slice(0, 2), to: move.slice(2, 4) });
            }

            if (!cancelled) {
                setIsReplaying(false);
            }
        };

        void runReplay();

        return () => {
            cancelled = true;
        };
    }, [dailyData.puzzle.start_fen, isReplaying, solutionUci]);

    const replayStatusCopy = dailyData.progress.status === Status.Solved
        ? 'This day was already solved.'
        : 'Past days are view-only and do not affect your streak.';

    return (
        <div className="daily-page">
            <div className="page-content replay-view">
                <InstrumentHeader
                    puzzleDate={dailyData.date}
                    attacker={dailyData.puzzle.attacker}
                    currentStep={0}
                    totalSteps={dailyData.puzzle.steps.length}
                    attemptsUsed={dailyData.progress.attempts_used}
                    status={dailyData.progress.status}
                />

                <Board
                    fen={boardFen}
                    orientation={dailyData.puzzle.attacker === 'w' ? 'white' : 'black'}
                    onMove={() => { }}
                    lastMove={lastMove ?? undefined}
                    disabled={true}
                />

                <div className="replay-note">
                    <p className="replay-title">Historical replay</p>
                    <p className="replay-copy">{replayStatusCopy}</p>
                </div>

                <div className="replay-buttons">
                    <Button
                        className="replay-button"
                        width="fixed"
                        onClick={() => setIsReplaying(true)}
                        disabled={isReplaying}
                    >
                        {isReplaying ? 'Playing...' : 'Replay line'}
                    </Button>
                    <Button
                        className="replay-button"
                        variant="secondary"
                        width="fixed"
                        onClick={() => {
                            setIsReplaying(false);
                            setBoardFen(dailyData.puzzle.start_fen);
                            setLastMove(null);
                        }}
                    >
                        Reset board
                    </Button>
                    <Button
                        className="replay-button"
                        variant="ghost"
                        width="fixed"
                        onClick={onBackToDaily}
                    >
                        Back to today
                    </Button>
                </div>
            </div>
        </div>
    );
}
